import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, Square, Play, Pause, Send, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { contact } from "@/config";
import { Spinner } from "@/components/primitives";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_SEC = 120;

const useWaveform = (canvasRef) => {
  const raf = useRef(null);
  const ctxRef = useRef(null);
  const start = (stream) => {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ac = new AC();
    const src = ac.createMediaStreamSource(stream);
    const an = ac.createAnalyser();
    an.fftSize = 256;
    src.connect(an);
    ctxRef.current = ac;
    const data = new Uint8Array(an.frequencyBinCount);
    const draw = () => {
      const cv = canvasRef.current;
      if (!cv) return;
      const g = cv.getContext("2d");
      an.getByteFrequencyData(data);
      g.clearRect(0, 0, cv.width, cv.height);
      const bars = 48, w = cv.width / bars;
      for (let i = 0; i < bars; i++) {
        const v = data[Math.floor((i / bars) * data.length)] / 255;
        const h = Math.max(2, v * cv.height);
        g.fillStyle = `rgba(201,165,74,${0.45 + v * 0.55})`;
        g.fillRect(i * w + 1, (cv.height - h) / 2, w - 2, h);
      }
      raf.current = requestAnimationFrame(draw);
    };
    draw();
  };
  const stop = () => { cancelAnimationFrame(raf.current); ctxRef.current?.close(); ctxRef.current = null; };
  return { start, stop };
};

export default function Voicemail({ open, onClose }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | recorded | sending
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const canvasRef = useRef(null);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const timer = useRef(null);
  const wave = useWaveform(canvasRef);

  const reset = () => {
    clearInterval(timer.current);
    wave.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    audioRef.current?.pause();
    setPhase("idle"); setSeconds(0); setBlob(null); setPlaying(false); setErr("");
  };
  useEffect(() => { if (!open) reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const startRec = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        setBlob(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
        stream.getTracks().forEach((t) => t.stop());
        wave.stop();
        setPhase("recorded");
      };
      recRef.current = rec;
      rec.start(250);
      wave.start(stream);
      setPhase("recording");
      setSeconds(0);
      timer.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= MAX_SEC) stopRec();
        return s + 1;
      }), 1000);
    } catch (e) {
      setErr(e?.name === "NotAllowedError" ? "Microphone permission was denied. Allow it in your browser to record." : "Recording isn't available in this browser.");
    }
  };
  const stopRec = () => { clearInterval(timer.current); if (recRef.current?.state === "recording") recRef.current.stop(); };

  const togglePlay = () => {
    if (!blob) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(URL.createObjectURL(blob));
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const send = async () => {
    if (!blob) return;
    setPhase("sending");
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(blob);
      });
      const res = await fetch(`${API}/api/voicemail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ audio_base64: b64, mime_type: blob.type, duration_sec: seconds, caller_name: name, caller_email: email }),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success(contact.successToast);
      onClose();
    } catch {
      setPhase("recorded");
      setErr("Objection — the message didn't go through. Try sending again.");
    }
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="voicemail-panel"
          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div className="card mx-auto mt-10 max-w-xl rounded-2xl p-6 sm:p-8">
            <div className="mb-5 flex items-center justify-between">
              <span className="label text-gold">Voicemail</span>
              <button data-testid="voicemail-close" onClick={onClose} aria-label="Close" className="text-gray2 hover:text-cream"><X className="h-4 w-4" /></button>
            </div>

            <div className="flex h-20 items-center justify-center rounded-xl border border-cream/8 bg-noir/60 px-4">
              {phase === "recording" ? (
                <canvas ref={canvasRef} width={480} height={56} className="h-14 w-full" data-testid="voicemail-waveform" />
              ) : phase === "recorded" || phase === "sending" ? (
                <div className="flex items-center gap-4 text-sm text-cream/80">
                  <button data-testid="voicemail-play" onClick={togglePlay} aria-label={playing ? "Pause" : "Play back"} className="grid h-10 w-10 place-items-center rounded-full border border-gold/50 text-gold">
                    {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  <span className="font-mono text-xs text-gray2">{mmss} recorded</span>
                  <button data-testid="voicemail-rerecord" onClick={reset} className="flex items-center gap-1 text-xs text-gray2 hover:text-cream"><RotateCcw className="h-3.5 w-3.5" /> Re-record</button>
                </div>
              ) : (
                <p className="text-sm text-gray2">Tap record and leave your message. Up to two minutes.</p>
              )}
            </div>

            <div className="mt-5 flex items-center justify-center gap-4">
              {phase === "recording" ? (
                <button data-testid="voicemail-stop" onClick={stopRec} className="btn btn-red">
                  <Square className="h-4 w-4 fill-current" /> Stop <span className="font-mono text-xs opacity-80">{mmss}</span>
                </button>
              ) : phase === "idle" ? (
                <button data-testid="voicemail-record" onClick={startRec} className="btn btn-red">
                  <Mic className="h-4 w-4" /> Record
                </button>
              ) : null}
            </div>

            {(phase === "recorded" || phase === "sending") && (
              <div className="mt-6 space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input data-testid="voicemail-name" className="input" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
                  <input data-testid="voicemail-email" className="input" type="email" placeholder="Your email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <button data-testid="voicemail-send" onClick={send} disabled={phase === "sending"} className="btn btn-red w-full disabled:opacity-60">
                  {phase === "sending" ? <Spinner /> : <Send className="h-4 w-4" />} {phase === "sending" ? "Sending…" : "Send voicemail"}
                </button>
              </div>
            )}

            {err && <p data-testid="voicemail-error" className="mt-4 text-center text-xs text-amber-200/90">{err}</p>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
